/**
 * Motion dice: shaking the phone without holding the screen, like dice in a cup. Pure (no
 * browser APIs): `MotionTracker` feeds it each reading.
 *
 * A swing is a burst of acceleration over `shakeAccel` (m/s²), lasting until it falls back
 * below 60% of that. Swings the opposite way to the one before count as half a shake (one full
 * shake: there and back); a swing the same way just adds to the one before. A pause of more
 * than `shakeGapMs` between swings starts the count again. After `shakeMinShakes` full shakes the
 * dice are let go (`shakeRelease`, Dice Lab):
 * - **0, a final hard flick:** a swing at least `shakeFlick` times as hard as the shake's swings
 *   so far throws, at the moment it ends (the hand's fastest). Stop without flicking: nothing.
 * - **1, stop shaking:** the phone still for `shakeStillMs` throws, as of the end of the last swing.
 * The throw itself is read like a flick (`readMotion`, as of the release moment).
 */
import type { MotionSample } from './toss';
import { tuning } from './tuning';

type Vec = [number, number, number];

/** A swing ended (`hit` 0 to 1, how hard: for the rattle's sound and buzz), or the dice were let go `at` (ms). */
export type ShakeEvent = { kind: 'swing'; hit: number } | { kind: 'release'; at: number };

/** Below this share of `shakeAccel` a swing is over (and the phone counts as still). */
const SWING_END = 0.6;

export class ShakeDetector {
  /** The swing under way: its hardest moment and which way. */
  private swing: { max: number; dir: Vec } | null = null;
  /** This shake's swings: how hard each was, which way, and when it ended. */
  private swings: { max: number; dir: Vec; end: number }[] = [];
  /** The last reading the phone wasn't still. */
  private loud = 0;
  private released = false;

  reset(): void {
    this.swing = null;
    this.swings = [];
    this.loud = 0;
    this.released = false;
  }

  /** Full shakes so far (there and back). */
  get shakes(): number {
    return Math.floor(this.swings.length / 2);
  }

  /** Being shaken now: a swing under way or one not long ago. */
  active(now: number): boolean {
    const last = this.swings[this.swings.length - 1];
    return !this.released && (!!this.swing || (!!last && now - last.end < tuning().shakeGapMs));
  }

  push(s: MotionSample): ShakeEvent | null {
    if (this.released) return null;
    const tu = tuning();
    const mag = Math.hypot(s.ax, s.ay, s.az);
    if (mag > tu.shakeAccel * SWING_END * 0.5) this.loud = s.t;
    const last = this.swings[this.swings.length - 1];
    if (!this.swing) {
      if (mag >= tu.shakeAccel) {
        // A new swing; a long pause before it starts the count again.
        if (last && s.t - last.end > tu.shakeGapMs) this.swings = [];
        this.swing = { max: mag, dir: [s.ax / mag, s.ay / mag, s.az / mag] };
        return null;
      }
      const enough = this.swings.length >= tu.shakeMinShakes * 2;
      if (tu.shakeRelease >= 1 && enough && last && s.t - Math.max(last.end, this.loud) >= tu.shakeStillMs) {
        this.released = true;
        return { kind: 'release', at: last.end };
      }
      return null;
    }
    if (mag > this.swing.max) this.swing = { max: mag, dir: [s.ax / mag, s.ay / mag, s.az / mag] };
    if (mag >= tu.shakeAccel * SWING_END) return null;
    // The swing is over.
    const done = { ...this.swing, end: s.t };
    this.swing = null;
    const before = this.swings;
    if (tu.shakeRelease < 1 && before.length >= tu.shakeMinShakes * 2) {
      const mean = before.reduce((n, w) => n + w.max, 0) / before.length;
      if (done.max >= mean * tu.shakeFlick) {
        this.released = true;
        return { kind: 'release', at: s.t };
      }
    }
    const prev = before[before.length - 1];
    const back = !prev || prev.dir[0] * done.dir[0] + prev.dir[1] * done.dir[1] + prev.dir[2] * done.dir[2] < 0;
    if (back) before.push(done);
    else before[before.length - 1] = { ...prev, max: Math.max(prev.max, done.max), end: done.end };
    return { kind: 'swing', hit: Math.min(1, done.max / (tu.shakeAccel * 2.5)) };
  }
}
